import { useMemo, type FC, type ReactNode } from 'react'
import { Link } from 'react-router'
import {
    Anchor,
    Badge,
    Box,
    Button,
    Card,
    Center,
    Group,
    Loader,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title,
    Tooltip,
    UnstyledButton,
} from '@mantine/core'
import {
    IconAlertTriangle,
    IconArrowRight,
    IconBroadcast,
    IconMessages,
    IconPlus,
    IconSparkles,
} from '@tabler/icons-react'
import { formatDistanceToNow } from 'date-fns'
import { useAuth0 } from '@auth0/auth0-react'
import { CtaButton } from '@/components/CtaButton'
import { EmptyState } from '@/components/EmptyState'
import { AttentionBanner } from '@/components/AttentionBanner'
import { matchColor } from '@/lib/matchColor'
import { useCreateNewAudience } from '@/features/thread/useCreateNewAudience'
import { useGetThreads } from '@/features/thread/queries'
import { segmentKey } from '@/features/segment/api'
import { useSegmentStubs } from '@/features/segment/queries'
import classes from './DashboardPage.module.css'

type ThreadState = 'attention' | 'ready' | 'exploring' | 'delivered'

const STATE_META: Record<ThreadState, { label: string; color: string }> = {
    attention: { label: 'Needs attention', color: 'warning' },
    ready: { label: 'Ready to distribute', color: 'info' },
    exploring: { label: 'Exploring', color: 'gray' },
    delivered: { label: 'Delivered', color: 'success' },
}

interface Pipeline {
    exploring: number
    ready: number
    live: number
    attention: number
}

// The calm, interpreted pipeline strip — inline counts that replace the old
// 4-big-number metric wall. Each navigates to where that slice lives.
const PIPELINE_ITEMS: {
    key: keyof Pipeline
    label: string
    color: string
    to: string
}[] = [
    { key: 'exploring', label: 'Exploring', color: 'gray', to: '/thread' },
    { key: 'ready', label: 'Ready to send', color: 'info', to: '/audiences' },
    { key: 'live', label: 'Live', color: 'success', to: '/distributions' },
    {
        key: 'attention',
        label: 'Need attention',
        color: 'warning',
        to: '/distributions',
    },
]

// Max items shown per dashboard list — these are at-a-glance previews; the full
// list lives on each section's own page (reached via "View all").
const MAX_RESUME = 4
const MAX_LIVE = 4
const MAX_PROBLEMS = 4
const MAX_SUGGESTIONS = 3

interface DashThread {
    threadId: string
    title: string
    lastUpdated: string
    segmentCount: number
    state: ThreadState
    repMatch: number
    liveDestinations: string[]
    lastRunAt?: string
    hasDraftModeling: boolean
}

interface Problem {
    key: string
    threadId: string
    failed: boolean
    title: string
    detail: string
    to: string
}

interface Suggestion {
    key: string
    threadId: string
    title: string
    detail: string
    actionLabel: string
    to: string
}

function greeting(hour: number): string {
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
}

// Join clauses the way a person would: "a", "a and b", "a, b, and c".
function humanJoin(items: string[]): string {
    if (items.length <= 1) return items[0] ?? ''
    if (items.length === 2) return `${items[0]} and ${items[1]}`
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

// The agent-voice, plain-language read of where things stand. Omits zero
// clauses; leans positive when nothing needs attention.
function buildSummary(p: Pipeline): string {
    const parts: string[] = []
    if (p.live > 0)
        parts.push(`${p.live} audience${p.live === 1 ? '' : 's'} live`)
    if (p.attention > 0) {
        parts.push(
            `${p.attention} ${p.attention === 1 ? 'needs' : 'need'} your attention`
        )
    }
    if (p.ready > 0) parts.push(`${p.ready} ready to send`)
    if (parts.length === 0) {
        if (p.exploring > 0) {
            return `You're exploring ${p.exploring} audience${
                p.exploring === 1 ? '' : 's'
            } — keep going, or send one when it's ready.`
        }
        return 'Start a conversation to build your first audience.'
    }
    const base = `You've got ${humanJoin(parts)}.`
    // Lean positive when nothing's wrong (a separate sentence reads cleaner than
    // folding "and healthy" into the live clause).
    return p.attention === 0 && p.live > 0
        ? `${base} Everything live is healthy.`
        : base
}

export function DashboardPage() {
    const { createNewAudience } = useCreateNewAudience()
    const { user } = useAuth0()
    const {
        data: threads,
        isLoading,
        isError,
        isFetching,
        refetch,
    } = useGetThreads()
    const { lifecycleFor, destinations } = useSegmentStubs()

    const firstName =
        user?.given_name ?? user?.name?.split(' ')[0] ?? user?.nickname ?? ''

    const data = useMemo(() => {
        const dashThreads: DashThread[] = []
        const problems: Problem[] = []
        const platformOf = (id: string) =>
            destinations.find((d) => d.id === id)?.platform ?? id

        for (const t of threads ?? []) {
            const title = t.state.threadTitle?.trim() || 'Untitled audience'
            const segments = t.state.audienceConfigurations ?? []
            const liveIds = new Set<string>()
            let hasAttention = false
            let hasDraftModeling = false
            let matchSum = 0
            let lastRunAt: string | undefined
            for (const config of segments) {
                const life = lifecycleFor(segmentKey(config), t.threadId)
                matchSum += life.estimatedMatchPct
                if (life.modeling?.status === 'draft') hasDraftModeling = true
                if (life.distributeStatus === 'attention') hasAttention = true
                for (const delivery of life.deliveries) {
                    const { distribution, latestRun, configState } = delivery
                    const status = latestRun?.status
                    if (
                        latestRun?.runAt &&
                        (!lastRunAt ||
                            Date.parse(latestRun.runAt) > Date.parse(lastRunAt))
                    ) {
                        lastRunAt = latestRun.runAt
                    }
                    if (status === 'succeeded' || status === 'partial') {
                        liveIds.add(distribution.destinationId)
                    }
                    if (status === 'failed' || configState === 'needs-config') {
                        problems.push({
                            key: distribution.id,
                            threadId: t.threadId,
                            failed: status === 'failed',
                            title: `${title} → ${platformOf(distribution.destinationId)}`,
                            detail:
                                latestRun?.nextStep ??
                                (status === 'failed'
                                    ? 'Delivery failed — retry the push'
                                    : 'Needs setup'),
                            to: `/distributions?dest=${distribution.destinationId}`,
                        })
                    }
                }
            }
            const state: ThreadState =
                segments.length === 0
                    ? 'exploring'
                    : hasAttention
                      ? 'attention'
                      : liveIds.size > 0
                        ? 'delivered'
                        : 'ready'
            dashThreads.push({
                threadId: t.threadId,
                title,
                lastUpdated: t.lastUpdated,
                segmentCount: segments.length,
                state,
                repMatch: segments.length
                    ? Math.round(matchSum / segments.length)
                    : 0,
                liveDestinations: [...liveIds].map(platformOf),
                lastRunAt,
                hasDraftModeling,
            })
        }

        const pipeline: Pipeline = {
            exploring: dashThreads.filter((d) => d.state === 'exploring')
                .length,
            ready: dashThreads.filter((d) => d.state === 'ready').length,
            live: dashThreads.filter((d) => d.liveDestinations.length > 0)
                .length,
            attention: dashThreads.filter((d) => d.state === 'attention')
                .length,
        }

        // "Live & healthy" — freshest push first (freshness is the visible trust
        // signal here), sorted before slicing so the shown few are the
        // most-recently-active, not whatever order threads arrived in.
        const live = dashThreads
            .filter((d) => d.liveDestinations.length > 0)
            .sort(
                (a, b) =>
                    (b.lastRunAt ? Date.parse(b.lastRunAt) : 0) -
                    (a.lastRunAt ? Date.parse(a.lastRunAt) : 0)
            )
            .slice(0, MAX_LIVE)

        // "What's next" — grounded, honest *moves on built audiences* (distribute
        // it, finish modeling, scale a low-match one). These are actions, distinct
        // from "Pick up where you left off" (re-enter a conversation).
        const suggestions: Suggestion[] = []
        for (const d of dashThreads) {
            if (d.state === 'ready') {
                suggestions.push({
                    key: `dist-${d.threadId}`,
                    threadId: d.threadId,
                    title: `Distribute “${d.title}”`,
                    detail: 'Built but never distributed — send it to a destination.',
                    actionLabel: 'Distribute',
                    to: '/audiences',
                })
            }
        }
        for (const d of dashThreads) {
            if (d.hasDraftModeling) {
                suggestions.push({
                    key: `model-${d.threadId}`,
                    threadId: d.threadId,
                    title: `Finish modeling “${d.title}”`,
                    detail: 'A lookalike is started but not ready yet.',
                    actionLabel: 'Open thread',
                    to: `/thread/${d.threadId}`,
                })
            }
        }
        for (const d of dashThreads) {
            if (d.segmentCount > 0 && d.repMatch < 70) {
                suggestions.push({
                    key: `lookalike-${d.threadId}`,
                    threadId: d.threadId,
                    title: `Build a lookalike for “${d.title}”`,
                    detail: `Matches ~${d.repMatch}% — a lookalike could lift reach.`,
                    actionLabel: 'Open thread',
                    to: `/thread/${d.threadId}`,
                })
            }
        }

        const shownProblems = [...problems]
            .sort((a, b) => Number(b.failed) - Number(a.failed))
            .slice(0, MAX_PROBLEMS)
        // One move per audience — keep the highest-priority (pushed in order:
        // distribute > finish modeling > scale) so the same audience isn't
        // suggested twice.
        const seenSuggestion = new Set<string>()
        const shownSuggestions = suggestions
            .filter((s) => {
                if (seenSuggestion.has(s.threadId)) return false
                seenSuggestion.add(s.threadId)
                return true
            })
            .slice(0, MAX_SUGGESTIONS)

        // Each thread belongs to one work surface. A problem (attention band) or a
        // next move ("What's next") outranks merely being a recent conversation, so
        // "Pick up where you left off" excludes anything already shown there — it
        // then reads cleanly as your most-recent conversations to re-enter, with no
        // duplicate audiences across sections.
        const claimed = new Set<string>([
            ...shownProblems.map((p) => p.threadId),
            ...shownSuggestions.map((s) => s.threadId),
        ])
        const resume = dashThreads
            .filter((d) => !claimed.has(d.threadId))
            .sort(
                (a, b) => Date.parse(b.lastUpdated) - Date.parse(a.lastUpdated)
            )
            .slice(0, MAX_RESUME)

        return {
            pipeline,
            resume,
            problems: shownProblems,
            problemCount: problems.length,
            live,
            suggestions: shownSuggestions,
            hasThreads: dashThreads.length > 0,
        }
    }, [threads, lifecycleFor, destinations])

    const summary = data.hasThreads
        ? buildSummary(data.pipeline)
        : 'Start a conversation to build your first audience.'

    return (
        <Stack gap='lg' h='100%'>
            {/* Greeting + the agent's plain-language read of where things stand. */}
            <Group
                justify='space-between'
                align='flex-start'
                style={{ flexShrink: 0 }}
            >
                <div>
                    <Title order={2}>
                        {greeting(new Date().getHours())}
                        {firstName ? `, ${firstName}` : ''}
                    </Title>
                    <Text size='md' c='dimmed' mt={4}>
                        {summary}
                    </Text>
                </div>
                <CtaButton
                    leftSection={<IconPlus size={16} />}
                    onClick={createNewAudience}
                >
                    Start a new audience
                </CtaButton>
            </Group>

            {isLoading ? (
                <Center style={{ flex: 1 }}>
                    <Loader />
                </Center>
            ) : isError ? (
                <Card withBorder style={{ flex: 1 }}>
                    <Center h='100%'>
                        <Stack align='center' gap='xs' py='xl' maw={440}>
                            <ThemeIcon
                                variant='light'
                                color='error'
                                size={56}
                                radius='md'
                            >
                                <IconAlertTriangle size={28} />
                            </ThemeIcon>
                            <Text fw={600}>Unable to load your dashboard</Text>
                            <Text c='dimmed' size='sm' ta='center'>
                                Something went wrong fetching your threads.
                                Please try again.
                            </Text>
                            <Button
                                variant='light'
                                mt='xs'
                                loading={isFetching}
                                onClick={() => void refetch()}
                            >
                                Retry
                            </Button>
                        </Stack>
                    </Center>
                </Card>
            ) : !data.hasThreads ? (
                <EmptyState
                    color='sage'
                    icon={<IconSparkles size={28} />}
                    title='Nothing here yet'
                    description='Start a conversation with the agent to ideate a strategy and build your first audience.'
                    action={
                        <CtaButton
                            leftSection={<IconPlus size={16} />}
                            mt='xs'
                            onClick={createNewAudience}
                        >
                            Start a new audience
                        </CtaButton>
                    }
                />
            ) : (
                <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    <Stack gap='xl'>
                        {/* Is it working? — problems first, honestly. */}
                        {data.problems.length > 0 && (
                            <AttentionBanner
                                title={`${data.problemCount} ${
                                    data.problemCount === 1
                                        ? 'thing needs'
                                        : 'things need'
                                } you`}
                            >
                                <Stack gap={8}>
                                    {data.problems.map((p) => (
                                        <Group
                                            key={p.key}
                                            justify='space-between'
                                            wrap='nowrap'
                                            gap='sm'
                                        >
                                            <Text
                                                size='sm'
                                                truncate
                                                style={{ minWidth: 0 }}
                                            >
                                                <Text span fw={500}>
                                                    {p.title}
                                                </Text>
                                                <Text span c='dimmed'>
                                                    {' '}
                                                    · {p.detail}
                                                </Text>
                                            </Text>
                                            <Anchor
                                                component={Link}
                                                to={p.to}
                                                size='sm'
                                                fw={500}
                                                c='warning'
                                                style={{ flexShrink: 0 }}
                                            >
                                                {p.failed ? 'Retry' : 'Fix'} →
                                            </Anchor>
                                        </Group>
                                    ))}
                                </Stack>
                                {data.problemCount > data.problems.length && (
                                    <Anchor
                                        component={Link}
                                        to='/distributions'
                                        size='sm'
                                        fw={500}
                                        c='warning'
                                        mt='sm'
                                        display='inline-block'
                                    >
                                        +
                                        {data.problemCount -
                                            data.problems.length}{' '}
                                        more →
                                    </Anchor>
                                )}
                            </AttentionBanner>
                        )}

                        {/* Where's my work? — the pipeline at a glance, interpreted. */}
                        <Group gap='xl' wrap='wrap'>
                            {PIPELINE_ITEMS.map((it) => {
                                const value = data.pipeline[it.key]
                                return (
                                    <UnstyledButton
                                        key={it.key}
                                        component={Link}
                                        to={it.to}
                                        className={classes.pill}
                                    >
                                        <Group
                                            gap={8}
                                            wrap='nowrap'
                                            align='baseline'
                                        >
                                            <Text
                                                fw={700}
                                                fz='xl'
                                                c={
                                                    value > 0
                                                        ? it.color
                                                        : 'dimmed'
                                                }
                                            >
                                                {value}
                                            </Text>
                                            <Text size='sm' c='dimmed'>
                                                {it.label}
                                            </Text>
                                        </Group>
                                    </UnstyledButton>
                                )
                            })}
                        </Group>

                        {/* What's next? — the proactive moves, front and center. */}
                        <Stack gap='xs'>
                            <SectionHeader
                                title="What's next"
                                subtitle='Suggested moves'
                                icon={<IconSparkles size={18} />}
                                iconColor='sage'
                            />
                            {data.suggestions.length === 0 ? (
                                <Text size='sm' c='dimmed'>
                                    You&apos;re all caught up. Start a new
                                    audience to explore a fresh angle.
                                </Text>
                            ) : (
                                <Stack gap='xs'>
                                    {data.suggestions.map((s) => (
                                        <SuggestionRow
                                            key={s.key}
                                            suggestion={s}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Stack>

                        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing='xl'>
                            {/* Is it working? — what's live (single pane). */}
                            <Stack gap='xs'>
                                <SectionHeader
                                    title='Live & healthy'
                                    subtitle='Delivering now'
                                    icon={<IconBroadcast size={18} />}
                                    iconColor='teal'
                                    action={
                                        <Button
                                            variant='subtle'
                                            size='compact-xs'
                                            component={Link}
                                            to='/distributions'
                                        >
                                            View all
                                        </Button>
                                    }
                                />
                                {data.live.length === 0 ? (
                                    <Text size='sm' c='dimmed'>
                                        Nothing is live yet — distribute an
                                        audience to see it here.
                                    </Text>
                                ) : (
                                    <Stack gap='xs'>
                                        {data.live.map((d) => (
                                            <LiveRow
                                                key={d.threadId}
                                                thread={d}
                                                to='/audiences'
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Stack>

                            {/* Where's my work? — continuity. */}
                            <Stack gap='xs'>
                                <SectionHeader
                                    title='Pick up where you left off'
                                    subtitle='Recent conversations'
                                    icon={<IconMessages size={18} />}
                                    action={
                                        <Button
                                            variant='subtle'
                                            size='compact-xs'
                                            component={Link}
                                            to='/thread'
                                        >
                                            View all
                                        </Button>
                                    }
                                />
                                {data.resume.length === 0 ? (
                                    <Text size='sm' c='dimmed'>
                                        You&apos;re all caught up — your next
                                        moves are above.
                                    </Text>
                                ) : (
                                    <Stack gap='xs'>
                                        {data.resume.map((d) => (
                                            <ResumeRow
                                                key={d.threadId}
                                                thread={d}
                                                to={`/thread/${d.threadId}`}
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Stack>
                        </SimpleGrid>
                    </Stack>
                </Box>
            )}
        </Stack>
    )
}

const SectionHeader: FC<{
    title: string
    subtitle?: string
    icon?: ReactNode
    iconColor?: string
    action?: ReactNode
}> = ({ title, subtitle, icon, iconColor = 'gray', action }) => (
    <Group justify='space-between' align='flex-start' wrap='nowrap'>
        <Group gap='sm' align='center' wrap='nowrap'>
            {icon && (
                <ThemeIcon
                    variant='light'
                    size='lg'
                    radius='md'
                    color={iconColor}
                >
                    {icon}
                </ThemeIcon>
            )}
            <div>
                <Text tt='uppercase' size='xs' fw={700} c='dimmed'>
                    {title}
                </Text>
                {subtitle && (
                    <Text size='xs' c='dimmed' opacity={0.7}>
                        {subtitle}
                    </Text>
                )}
            </div>
        </Group>
        {action}
    </Group>
)

function deliveryText(thread: DashThread): string {
    if (thread.segmentCount === 0) return 'No segments yet'
    const seg = `${thread.segmentCount} segment${thread.segmentCount === 1 ? '' : 's'}`
    if (thread.liveDestinations.length > 0) {
        return `${seg} · live on ${thread.liveDestinations.length}`
    }
    return `${seg} · not yet distributed`
}

const ResumeRow: FC<{ thread: DashThread; to: string }> = ({ thread, to }) => {
    const meta = STATE_META[thread.state]
    return (
        <Card
            withBorder
            padding='sm'
            radius='md'
            component={Link}
            to={to}
            className={`${classes.row} mantine-focus-auto`}
        >
            <Group justify='space-between' wrap='nowrap'>
                <Stack gap={2} style={{ minWidth: 0 }}>
                    <Text size='sm' fw={500} truncate>
                        {thread.title}
                    </Text>
                    <Text size='xs' c='dimmed'>
                        {deliveryText(thread)} ·{' '}
                        {formatDistanceToNow(new Date(thread.lastUpdated), {
                            addSuffix: true,
                        })}
                    </Text>
                </Stack>
                <Badge size='sm' variant='light' color={meta.color}>
                    {meta.label}
                </Badge>
            </Group>
        </Card>
    )
}

const LiveRow: FC<{ thread: DashThread; to: string }> = ({ thread, to }) => (
    <Card
        withBorder
        padding='sm'
        radius='md'
        component={Link}
        to={to}
        className={`${classes.row} mantine-focus-auto`}
    >
        <Group justify='space-between' wrap='nowrap' align='flex-start'>
            <Stack gap={4} style={{ minWidth: 0 }}>
                <Text size='sm' fw={500} truncate>
                    {thread.title}
                </Text>
                <Group gap={4} wrap='nowrap'>
                    {thread.liveDestinations.map((p) => (
                        <Badge
                            key={p}
                            size='xs'
                            variant='light'
                            color='success'
                        >
                            {p}
                        </Badge>
                    ))}
                </Group>
                {thread.lastRunAt && (
                    <Text size='xs' c='dimmed'>
                        last push{' '}
                        {formatDistanceToNow(new Date(thread.lastRunAt), {
                            addSuffix: true,
                        })}
                    </Text>
                )}
            </Stack>
            <Text size='sm' c={matchColor(thread.repMatch)} fw={600}>
                {thread.repMatch}%
            </Text>
        </Group>
    </Card>
)

const SuggestionRow: FC<{ suggestion: Suggestion }> = ({ suggestion }) => (
    <Card withBorder padding='sm' radius='md' className={classes.row}>
        <Group justify='space-between' wrap='nowrap' align='center'>
            <Stack gap={2} style={{ minWidth: 0 }}>
                <Text size='sm' fw={500} truncate>
                    {suggestion.title}
                </Text>
                <Text size='xs' c='dimmed'>
                    {suggestion.detail}
                </Text>
            </Stack>
            <Tooltip label={suggestion.actionLabel}>
                <Button
                    size='compact-sm'
                    variant='light'
                    component={Link}
                    to={suggestion.to}
                    rightSection={<IconArrowRight size={14} />}
                >
                    {suggestion.actionLabel}
                </Button>
            </Tooltip>
        </Group>
    </Card>
)
